import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { SeatingItemPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import { SeatingSelectReturnType } from "@shared/modules/seating/types/seating-types";
import { useCallback, useMemo, useState } from "react";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import {
  DefaultSeatSelectModalProps,
  MapItemType,
} from "./DefaultSeatSelectModal.props";

export function useDefaultSeatSelectModal(props: DefaultSeatSelectModalProps) {
  const { itemsOnSeat, ticketAuthArray, onClose, resolve } = props;
  const { seatingServiceState } = useSeatingState();
  const { $permissions } = itemsOnSeat;
  const { itemGroups, areAllRenewalItemGroups } = useBasketService();

  // this state is used to store the item permission that is selected
  // we use the itemPermissions along with the customerSelected state to resolve the data
  const [itemPermission, setItemPermission] =
    useState<SeatingSelectReturnType | null>(null);

  const selectItemPermission = useCallback(
    ({
      itemTypeId,
      permissionId,
      minPurchaseLimit,
      requestAmount,
      pwycPrice,
    }: {
      itemTypeId: number;
      permissionId: number;
      minPurchaseLimit?: number;
      requestAmount?: number;
      pwycPrice?: string | null;
    }) => {
      if (areAllRenewalItemGroups) {
        setItemPermission({
          itemTypeId,
          permissionId,
          requestAmount,
          ticketAuthArray,
          minPurchaseLimit,
          payWhatYouCanPrice: pwycPrice,
        });
      } else {
        // close the modal and return data
        resolve({
          itemTypeId,
          permissionId,
          requestAmount,
          ticketAuthArray,
          minPurchaseLimit,
          payWhatYouCanPrice: pwycPrice,
        });
      }
    },
    [resolve, ticketAuthArray, areAllRenewalItemGroups]
  );

  // item cards displayed on the selection modal
  const seatItems: Array<MapItemType> = useMemo(() => {
    // default permissions array is $permissions
    let permissionsArray = $permissions;

    // if there are seats in the pending cart,
    // get the item type in the pending cart and only show that item type on the selection modal
    if (seatingServiceState.pendingCartPermissions.length > 0) {
      permissionsArray = $permissions.filter(
        (p) =>
          p.seatingItemTypeId ===
          seatingServiceState.pendingCartPermissions[0].itemTypeId
      );
    }

    // if there are waitlisted and non-waitlisted items mixed on the seat,
    // this will remove the waitlisted items
    permissionsArray = permissionsArray.filter(
      (p) =>
        !seatingServiceState.itemTypeMap[
          p.seatingItemTypeId as keyof typeof seatingServiceState.itemTypeMap
        ].waitlist
    );

    return permissionsArray.map((p) => {
      const itemType: SeatingItemPropertyMap =
        seatingServiceState.itemTypeMap[
          p.seatingItemTypeId as keyof typeof seatingServiceState.itemTypeMap
        ];
      return {
        ...itemType,
        permissionId: Number(p.seatingItemPermissionId),
      };
    });
  }, [$permissions, seatingServiceState]);

  const cancelModal = () => {
    onClose(null);
  };

  const [screen, setScreen] = useState<"select-item" | "select-customer">(
    "select-item"
  );

  const handleScreen = (screen: "select-item" | "select-customer") => {
    setScreen(screen);
  };

  const [customerSelected, setCustomerSelected] =
    useState<TicketItemGroupType | null>(null);
  const handleSelectCustomer = (itemGroup: TicketItemGroupType) => {
    setCustomerSelected(itemGroup);
  };

  const handleUnselectCustomer = () => {
    setCustomerSelected(null);
  };

  return {
    selectItemPermission,
    cancelModal,
    seatItems,
    itemPermission,
    customerSelected,
    handleSelectCustomer,
    handleUnselectCustomer,
    handleScreen,
    screen,
    itemGroups,
    areAllRenewalItemGroups,
  };
}
